import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Users, Briefcase, ChevronRight, Check } from 'lucide-react';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { MarketingPricingCompare } from '@/components/marketing/MarketingPricingCompare';
import { MarketingPlanCards } from '@/components/marketing/MarketingPlanCards';
import { MarketingAssistantBand } from '@/components/marketing/MarketingAssistantBand';
import { MarketingStatsBar } from '@/components/marketing/MarketingStatsBar';
import {
  MarketingCoreFeatures,
  MarketingHumanLoopBand,
  MarketingProductTour,
} from '@/components/marketing/MarketingCoreFeatures';
import { MockApplicationsScores } from '@/components/marketing/MarketingProductMocks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { PageSeo } from '@/components/seo/PageSeo';
import { homeSeo } from '@/lib/seo/marketingSeo';

const BRAND_GRADIENT =
  'linear-gradient(135deg, #0f172a 0%, #1e1b4b 35%, #312e81 65%, #4338ca 100%)';

export default function MarketingHomePage() {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const hash = location.hash?.replace(/^#/, '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (!el) return;
    const tId = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(tId);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const seo = homeSeo(language);

  return (
    <div id="top" className="min-h-screen bg-brand-surface-light text-foreground">
      <PageSeo title={seo.title} description={seo.description} path="/" />
      <MarketingNav />

      <section className="container py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('marketing.hero.audience')}</p>
            <p className="text-sm font-medium text-brand-accent">{t('marketing.hero.tagline')}</p>
            <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight leading-[1.1]">
              {t('marketing.hero.titleLine1')}
              <br />
              {t('marketing.hero.titleLine2')}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">{t('marketing.hero.valueProp')}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="bg-brand-accent hover:bg-brand-accent/90" asChild>
                <Link to="/signup">{t('marketing.hero.ctaTrial')}</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="#pricing">{t('marketing.hero.ctaSecondary')}</a>
              </Button>
            </div>
            <ul className="text-sm text-muted-foreground space-y-2" aria-label={language === 'it' ? 'Vantaggi prova gratuita' : 'Free trial benefits'}>
              {(['marketing.hero.proof1', 'marketing.hero.proof2', 'marketing.hero.proof3'] as const).map((key) => (
                <li key={key} className="flex gap-2 items-start">
                  <Check className="h-4 w-4 text-brand-accent shrink-0 mt-0.5" aria-hidden />
                  {t(key)}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            {!reducedMotion && (
              <div
                className="absolute -inset-4 rounded-3xl opacity-30 blur-3xl -z-10"
                style={{ background: 'radial-gradient(circle, #7c3aed, transparent 70%)' }}
                aria-hidden
              />
            )}
            <MockApplicationsScores caption={t('marketing.product.caption.applications')} />
          </div>
        </div>
      </section>

      <MarketingStatsBar />

      <section className="container py-16 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-3">
          <h2 className="font-display font-bold text-2xl md:text-3xl">{t('marketing.steps.title')}</h2>
          <p className="text-muted-foreground leading-relaxed">{t('marketing.steps.subtitle')}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { n: '1', title: t('marketing.steps.step1.title'), body: t('marketing.steps.step1.body'), icon: Briefcase },
            { n: '2', title: t('marketing.steps.step2.title'), body: t('marketing.steps.step2.body'), icon: Users },
            { n: '3', title: t('marketing.steps.step3.title'), body: t('marketing.steps.step3.body'), icon: ChevronRight },
          ].map((step) => (
            <Card key={step.n} className="border-border/60 bg-card">
              <CardContent className="pt-6 space-y-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-accent/10 text-brand-accent font-display font-bold">
                    {step.n}
                  </span>
                  <step.icon className="h-5 w-5 text-brand-accent" aria-hidden />
                </div>
                <h3 className="font-semibold">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <Button size="lg" className="bg-brand-accent hover:bg-brand-accent/90" asChild>
            <Link to="/signup">{t('marketing.steps.cta')}</Link>
          </Button>
        </div>
      </section>

      <MarketingAssistantBand />
      <MarketingProductTour />
      <MarketingCoreFeatures />
      <MarketingHumanLoopBand />

      <section className="container py-12">
        <div className="rounded-xl border border-border/60 bg-card p-8 text-center max-w-3xl mx-auto space-y-4">
          <h2 className="font-display font-bold text-xl">{t('marketing.trust.title')}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{t('marketing.trust.body')}</p>
          <Button variant="link" className="text-brand-accent" asChild>
            <Link to="/faq">{t('marketing.trust.ctaFaq')}</Link>
          </Button>
        </div>
      </section>

      <section id="agency" className="scroll-mt-24 container py-12 md:py-16">
        <Card className="border-l-4 border-l-brand-teal border-border/60 bg-card overflow-hidden">
          <CardContent className="p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="space-y-4 max-w-xl">
              <h2 className="font-display font-bold text-2xl">{t('marketing.agency.title')}</h2>
              <p className="text-muted-foreground leading-relaxed">{t('marketing.agency.body')}</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {(['marketing.agency.bullet1', 'marketing.agency.bullet2', 'marketing.agency.bullet3'] as const).map((key) => (
                  <li key={key} className="flex gap-2">
                    <span className="text-brand-accent font-bold">•</span>
                    {t(key)}
                  </li>
                ))}
              </ul>
            </div>
            <Button size="lg" variant="outline" className="border-brand-primary text-brand-primary shrink-0" asChild>
              <Link to="/contact?type=agency">{t('marketing.agency.cta')}</Link>
            </Button>
          </CardContent>
        </Card>
      </section>

      <section id="pricing" className="scroll-mt-24 container py-16 md:py-20">
        <h2 className="font-display font-bold text-2xl md:text-3xl text-center mb-3">{t('marketing.pricing.title')}</h2>
        <p className="text-center text-muted-foreground text-sm max-w-2xl mx-auto mb-10 leading-relaxed">
          {t('marketing.pricing.subtitle')}
        </p>
        <MarketingPlanCards />
        <p className="text-center text-xs text-muted-foreground max-w-2xl mx-auto mt-8 leading-relaxed">
          {t('marketing.pricing.reassurance')}
        </p>
        <div className="mt-12">
          <MarketingPricingCompare />
        </div>
      </section>

      <section className="py-16 md:py-20 text-white" style={{ background: BRAND_GRADIENT }}>
        <div className="container text-center space-y-6 max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{t('marketing.final.title')}</h2>
          <p className="text-white/80 leading-relaxed">{t('marketing.final.subtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-brand-primary hover:bg-white/90" asChild>
              <Link to="/signup">{t('marketing.final.ctaTrial')}</Link>
            </Button>
            <Button size="lg" variant="ghost" className="text-white border border-white/25 hover:bg-white/10" asChild>
              <Link to="/login">{t('marketing.final.login')}</Link>
            </Button>
          </div>
          <p className="text-sm text-white/70">{t('marketing.final.note')}</p>
        </div>
      </section>

      <MarketingFooter />

      <div
        className={cn(
          'md:hidden fixed bottom-0 inset-x-0 z-50 p-3 border-t border-border/60 bg-background/95 backdrop-blur',
        )}
      >
        <Button className="w-full bg-brand-accent hover:bg-brand-accent/90" size="lg" asChild>
          <Link to="/signup">{t('marketing.nav.trialCta')}</Link>
        </Button>
      </div>
      <div className="h-20 md:hidden" aria-hidden />
    </div>
  );
}
